import React, { useCallback, useEffect, useState } from 'react';
import { Box, Button, Chip, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ExpenseCardService } from '../../../services/expenseCard.service';
import { CardTransactionDTO } from '../../../interfaces/expense.interface';
import { useToast } from '../../../context/ToastContext';
import { formatAmount } from '../../../utils/expenseDisplay';

/** The holder's own card transactions: claim them on an expense claim, or mark personal spend. */
const MyCardTransactions: React.FC = () => {
    const toast = useToast();
    const navigate = useNavigate();
    const [rows, setRows] = useState<CardTransactionDTO[]>([]);

    const reload = useCallback(async () => {
        try {
            setRows(await ExpenseCardService.listMyTransactions());
        } catch (e: any) {
            toast.showApiError(e, 'Failed to load card transactions');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => { reload(); }, [reload]);

    const act = async (fn: () => Promise<unknown>, ok: string) => {
        try { await fn(); toast.showSuccess(ok); await reload(); } catch (e: any) { toast.showApiError(e, 'Action failed'); }
    };

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }} data-help-page="expense.myCards.page">
            <Typography variant="h5" sx={{ mb: 0.5 }}>My card transactions</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Add a transaction to an expense claim (line type: Corporate card), or mark it personal if it was not business spend.
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                <Button variant="contained" data-help="expense.myCards.newClaim" onClick={() => navigate('/expense/my-claims/new')}>New expense claim</Button>
            </Box>
            <TableContainer component={Paper} data-help-section="expense.myCards.table">
                <Table size="small">
                    <TableHead><TableRow><TableCell data-help="expense.myCards.col.date">Date</TableCell><TableCell data-help="expense.myCards.col.card">Card</TableCell><TableCell data-help="expense.myCards.col.description">Description</TableCell><TableCell align="right" data-help="expense.myCards.col.amount">Amount</TableCell><TableCell data-help="expense.myCards.col.status">Status</TableCell><TableCell data-help="expense.myCards.col.actions" /></TableRow></TableHead>
                    <TableBody>
                        {rows.length === 0 && <TableRow><TableCell colSpan={6}>No card transactions.</TableCell></TableRow>}
                        {rows.map(t => (
                            <TableRow key={t.id}>
                                <TableCell data-help="expense.myCards.col.date">{t.txnDate}</TableCell><TableCell data-help="expense.myCards.col.card">{t.cardLabel}</TableCell>
                                <TableCell data-help="expense.myCards.col.description">{t.description ?? t.reference}</TableCell>
                                <TableCell align="right" data-help="expense.myCards.col.amount">{formatAmount(t.amount)}</TableCell>
                                <TableCell data-help="expense.myCards.col.status"><Chip size="small" label={t.status} /></TableCell>
                                <TableCell align="right">
                                    {t.status === 'UNMATCHED' && <Button size="small" data-help="expense.myCards.action.markPersonal" onClick={() => act(() => ExpenseCardService.markPersonal(t.id), 'Marked personal')}>Mark personal</Button>}
                                    {t.status === 'PERSONAL' && <Button size="small" data-help="expense.myCards.action.undoPersonal" onClick={() => act(() => ExpenseCardService.unmarkPersonal(t.id), 'Reverted')}>Undo personal</Button>}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default MyCardTransactions;
